import { ref } from 'vue';
import type { Ref } from 'vue';

import { functionalActivityApi } from '@/entities/FunctionalActivity';
import { downloadBlobFile, useCustomToast, type SearchFilter } from '@/shared/lib';

type UseFunctionalActivitiesExportParams = {
  searchTerm: Ref<string>;
  sort: Ref<string>;
  order: Ref<string>;
  filter: Ref<SearchFilter>;
};

export const useFunctionalActivitiesExport = ({
  searchTerm,
  sort,
  order,
  filter,
}: UseFunctionalActivitiesExportParams) => {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    try {
      toast.showWarningToast({
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      });

      const currentFilter = filter.value;
      const { blob, exportedCount, filename } =
        await functionalActivityApi.exportFunctionalActivitiesExcel({
          search: searchTerm.value.trim() || undefined,
          sort: sort.value || undefined,
          order: order.value || undefined,
          active:
            currentFilter.active !== undefined
              ? String(currentFilter.active) === 'true'
              : undefined,
          branchId: currentFilter.branchId ? Number(currentFilter.branchId) : undefined,
          departmentId: currentFilter.departmentId
            ? Number(currentFilter.departmentId)
            : undefined,
        });

      downloadBlobFile(blob, filename);

      const successMessage =
        exportedCount === 1
          ? '1 registro exportado'
          : `${exportedCount} registros exportados`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch {
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: 'Tente novamente.',
        type: 'error',
      });
    } finally {
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
  };
};
